import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import type { Database } from "@/utils/supabase.types";
import _ from "lodash";

type UserPreferencesRow =
  Database["public"]["Tables"]["user_preferences"]["Row"];

interface UserPreferencesState {
  // Modal state
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;

  // Nudge settings
  smallNudge: number;
  largeNudge: number;
  setSmallNudge: (value: number) => void;
  setLargeNudge: (value: number) => void;

  // Github access token
  githubAccessToken: string;
  setGithubAccessToken: (token: string) => void;

  // Supabase sync
  loadPreferences: (userId: string) => Promise<void>;
  saveToSupabase: (userId: string) => Promise<void>;
  isLoading: boolean;
}

const saveToSupabaseDebounced = _.debounce(
  async (userId: string, state: Partial<UserPreferencesState>) => {
    try {
      const supabase = useSupabaseStore.getState().supabaseClient;
      if (!supabase) {
        console.warn(
          "Supabase client not available, skipping preferences save",
        );
        return;
      }

      const preferencesData = {
        user_id: userId,
        small_nudge: state.smallNudge,
        large_nudge: state.largeNudge,
        github_access_token: state.githubAccessToken || null,
      };

      await supabase
        .from("user_preferences")
        .upsert(preferencesData, { onConflict: "user_id" });
    } catch (error) {
      console.error("Error saving preferences to Supabase:", error);
    }
  },
  1000,
);

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set, get) => ({
      // Modal state (not persisted)
      isModalOpen: false,
      openModal: () => set({ isModalOpen: true }),
      closeModal: () => set({ isModalOpen: false }),

      // Nudge settings
      smallNudge: 1,
      largeNudge: 10,
      setSmallNudge: (value: number) => {
        set({ smallNudge: value });
        // Note: Auto-save will be handled by the component that calls this
      },
      setLargeNudge: (value: number) => {
        set({ largeNudge: value });
        // Note: Auto-save will be handled by the component that calls this
      },

      // Github access token
      githubAccessToken: "",
      setGithubAccessToken: (token: string) => {
        set({ githubAccessToken: token });
        // Note: Auto-save will be handled by the component that calls this
      },

      // Loading state
      isLoading: false,

      // Load preferences from Supabase
      loadPreferences: async (userId: string) => {
        set({ isLoading: true });
        try {
          const supabase = useSupabaseStore.getState().supabaseClient;
          if (!supabase) {
            console.warn(
              "Supabase client not available, skipping preferences load",
            );
            set({ isLoading: false });
            return;
          }

          const { data, error } = await supabase
            .from("user_preferences")
            .select("*")
            .eq("user_id", userId)
            .single();

          if (data && !error) {
            set({
              smallNudge: data.small_nudge || 1,
              largeNudge: data.large_nudge || 10,
              githubAccessToken: data.github_access_token || "",
            });
          }
        } catch (error) {
          console.error("Error loading preferences from Supabase:", error);
        } finally {
          set({ isLoading: false });
        }
      },

      // Save to Supabase (manual save)
      saveToSupabase: async (userId: string) => {
        const state = get();
        await saveToSupabaseDebounced(userId, state);
      },
    }),
    {
      name: "user-preferences-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        smallNudge: state.smallNudge,
        largeNudge: state.largeNudge,
        githubAccessToken: state.githubAccessToken,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn("Failed to load user preferences from storage:", error);
        }
      },
    },
  ),
);
